// Role-specific preparation and ordinary business follow-up for workbook rows MP215-MP429.
// These examples describe a provider's workflow, not specialist Offlico capabilities.
export const professionWorkflowB = Object.freeze({
  MP215: {
    before: 'Ask whether the oil tank visit is for a suspected leak, routine check or replacement quote, then allow for the right site contact and access time.',
    after: 'Send the customer the agreed next appointment or quotation date, while keeping any technical findings and required certificates in your own system.',
  },
  MP216: {
    before: 'Check when the occupant can show you the affected rooms and whether pets or business activity will affect the pest inspection slot.',
    after: 'Put the agreed follow-up inspection in the diary and explain when the customer will hear from you about any further treatment.',
  },
  MP217: {
    before: 'Confirm that someone can identify the septic tank cover and that your vehicle can reach the property during the booked callout.',
    after: 'Separate any return repair or emptying visit from the initial assessment, and send the customer the next agreed date and price.',
  },
  MP218: {
    before: 'Establish who will be present to point out the items for clearance and reserve enough loading time for the property\'s access.',
    after: 'Confirm when the clearance is finished and handle the invoice, while keeping any required waste paperwork in your usual specialist process.',
  },
  MP219: {
    before: 'Arrange an escort who can open the relevant plant rooms and agree a visit window that suits the building manager.',
    after: 'Schedule the next sampling or review date with the site contact, keeping test results and statutory records in your specialist system.',
  },
  MP220: {
    before: 'Agree whether the arborist is coming to inspect a tree or carry out approved work, and allow time for crew and equipment access.',
    after: 'If work is staged, confirm the next crew date and customer payment plan without treating the booking diary as a tree safety record.',
  },
  MP221: {
    before: 'Book the boundary measure separately from fitting, and check when the customer can give access to both sides of the proposed fence.',
    after: 'Once the materials and installation days are agreed, send the customer those dates and keep the quote or invoice easy to find.',
  },
  MP222: {
    before: 'Find out whether the garden designer needs an initial walk-through or a review of proposed plans, then invite the relevant decision-maker.',
    after: 'Give the client a clear date for the next design discussion and follow up on the agreed fee outside the design files themselves.',
  },
  MP223: {
    before: 'Ask whether the mower or other garden machine will be left on site or collected, and book time for the appropriate handover.',
    after: 'Let the owner know when the repair estimate or return collection will be ready, keeping parts work distinct from the original visit.',
  },
  MP224: {
    before: 'For a gardening round, check which seasonal tasks the customer wants this time and whether the usual gate and green-waste arrangements still apply.',
    after: 'Confirm the next maintenance date and send any charge for this visit, making a separate booking if extra work needs more time.',
  },
  MP225: {
    before: 'Check which entrances and paths the gritting crew must cover and who will confirm that a forecast-triggered visit is needed.',
    after: 'Tell the site contact when the visit took place and keep the next weather-dependent slot flexible rather than promising a fixed treatment outcome.',
  },
  MP226: {
    before: 'Review each grounds site on the route for opening hours, gate access and the maintenance tasks due on that particular visit.',
    after: 'Keep the next site visit and any extra quoted work separate, so recurring maintenance does not absorb an unplanned project.',
  },
  MP227: {
    before: 'Use the first irrigation visit to confirm the water connection and garden layout, then book fitting only when the scope is settled.',
    after: 'Arrange a handover or adjustment slot after installation and send the customer the agreed invoice for the completed stage.',
  },
  MP228: {
    before: 'Clarify whether the landscape architect is visiting for a site survey, concept review or detailed design decision before inviting the client.',
    after: 'Put the next design milestone in the calendar and keep fee follow-up linked to the agreed stage, not a generic garden visit.',
  },
  MP229: {
    before: 'Check that materials can be delivered to the landscape site and leave realistic space in the diary for groundwork before planting.',
    after: 'Agree the next installation stage with the client and adjust its date if weather or unfinished preparation changes the sequence.',
  },
  MP230: {
    before: 'Ask which lawn treatment is booked and whether the grass will be accessible and suitable to work on in the expected weather.',
    after: 'Confirm the next planned lawn visit and its price, while keeping treatment choices and any specialist application records in your own process.',
  },
  MP231: {
    before: 'Reserve a separate paving survey if measurements or ground preparation are unresolved, and check where deliveries can be unloaded.',
    after: 'Tell the customer when laying can start after preparation, then follow up on any staged invoice against the agreed work.',
  },
  MP232: {
    before: 'Ask whether the pond visit is for cleaning, planting or equipment work so the appointment allows for the right tools and site access.',
    after: 'Book a separate return date if the pump or planting needs another visit, and confirm the cost before adding that time to the diary.',
  },
  MP233: {
    before: 'Check whether the swimming pool or spa can be taken out of use during the visit and who can open the plant area.',
    after: 'Agree a return appointment if a part or further testing is needed, with technical logs kept in the provider’s own service system.',
  },
  MP234: {
    before: 'Ask the client which entrances and rooms should be reviewed, and arrange a time when the person using the space can take part.',
    after: 'Set a date to discuss the access consultant’s recommendations, keeping assessment detail in the appropriate professional record.',
  },
  MP235: {
    before: 'Reserve an acoustic site visit when the relevant noise can actually be heard, and confirm who can provide access to measurement areas.',
    after: 'Schedule the findings discussion once the measurements are processed, with the technical report held outside the booking diary.',
  },
  MP236: {
    before: 'Choose an air-quality visit window that reflects the activity being assessed and check that the sampling locations will be available.',
    after: 'Agree when the client should expect a results discussion, while keeping monitoring data and formal reports in your own system.',
  },
  MP237: {
    before: 'Establish whether the architect needs to measure the property or meet to settle design choices, then book the right people and time.',
    after: 'Arrange the next drawing review with the client and follow up on the agreed design-stage fee without storing plans in the diary.',
  },
  MP238: {
    before: 'Check which architectural drawings are current before booking a technologist’s site measure or technical coordination meeting.',
    after: 'Give the client a date for the next revision discussion, keeping detailed drawing versions in the project’s own files.',
  },
  MP239: {
    before: 'Agree the rooms the asbestos surveyor can enter and a quiet access window, especially where occupants need to vacate an area.',
    after: 'Arrange a separate report discussion or further visit if needed, and keep survey evidence within the appropriate specialist records.',
  },
  MP240: {
    before: 'Confirm the building surveyor can reach the loft, roof spaces and outbuildings included in the instruction before allocating site time.',
    after: 'Set expectations for the report handover and any client call, with survey findings retained in the professional reporting system.',
  },
  MP241: {
    before: 'Make sure the civil engineer has the right site contact and the current plans before reserving time to inspect levels or drainage.',
    after: 'Book the next design or site coordination meeting once measurements are reviewed, and keep engineering outputs in project files.',
  },
  MP242: {
    before: 'Ask the owner to make the affected rooms and timber accessible, then allow enough inspection time before discussing possible remedial work.',
    after: 'Agree when the damp findings will be discussed and keep the inspection report separate from routine appointment and invoice admin.',
  },
  MP243: {
    before: 'Check that the energy assessor can view the relevant heating controls, windows and insulation areas during the property visit.',
    after: 'Set a date to share the assessment output, while handling certificates and evidence in the assessor’s usual professional system.',
  },
  MP244: {
    before: 'Arrange the fire-risk walkthrough when a knowledgeable building contact can open escape routes and explain normal occupancy.',
    after: 'Book a findings discussion or review date and keep the risk assessment and any compliance actions in a suitable specialist process.',
  },
  MP245: {
    before: 'Tell the interior design client whether the session covers measurements, samples or final choices so the right rooms and people are ready.',
    after: 'Confirm the next presentation or fitting discussion, with supplier orders and design files managed outside the booking schedule.',
  },
  MP246: {
    before: 'Agree which boundary the land surveyor must measure and arrange permission to enter all relevant parts of the site.',
    after: 'Set a handover date for the survey drawing and a separate client call if further measurements or clarification are needed.',
  },
  MP247: {
    before: 'Ask the keyholder to identify the areas for the property inspection and ensure access is available at the booked time.',
    after: 'Confirm when the client will receive the inspection report and arrange any return visit as a new appointment, not an assumed extension.',
  },
  MP248: {
    before: 'Check whether the inventory clerk is attending a check-in, check-out or interim inspection and when keys can be collected.',
    after: 'Plan the report handover around the tenancy change, keeping condition evidence in the inventory system rather than general booking notes.',
  },
  MP249: {
    before: 'Make sure the quantity surveyor and client are discussing the same drawing revision before a cost review or site measure is booked.',
    after: 'Agree the next valuation or variation meeting and follow up on fees, with detailed cost schedules held in project records.',
  },
  MP250: {
    before: 'Arrange the retrofit assessment when the assessor can inspect the building fabric and heating setup without restricted access.',
    after: 'Book a separate call to explain proposed measures, keeping the technical assessment in the appropriate professional system.',
  },
  MP251: {
    before: 'Check which retrofit stage the property has reached and invite the relevant installer or owner to the coordination visit.',
    after: 'Confirm the next project handover date and keep assessment evidence and measure sign-offs in the specialist project record.',
  },
  MP252: {
    before: 'Ask the structural engineer to identify whether the visit concerns an existing defect or a proposed alteration, with plans available on site.',
    after: 'Arrange a findings call or design review after inspection, while retaining calculations and formal advice in engineering files.',
  },
  MP253: {
    before: 'Pick a thermal survey time when indoor and outdoor temperatures make imaging worthwhile, and ask for clear access to the target areas.',
    after: 'Agree a date to explain the images and possible next inspection, keeping technical imagery in the surveyor’s reporting system.',
  },
  MP254: {
    before: 'Ask where the tractor or other farm machine is parked and whether the operator can make it available for a safe diagnostic slot.',
    after: 'Tell the farmer when parts or a second visit can be arranged, keeping the repair invoice linked to the correct machine.',
  },
  MP255: {
    before: 'Check the vehicle’s location and whether the auto electrician can test it without blocking a busy driveway or workshop bay.',
    after: 'If diagnosis points to a later fitting, book that as a separate visit and give the owner a clear estimate first.',
  },
  MP256: {
    before: 'Agree whether the bicycle mechanic will visit the rider or receive the bike elsewhere, and allow time to inspect the reported fault.',
    after: 'Confirm any parts order and collection slot with the rider, keeping that handover separate from the repair appointment.',
  },
  MP257: {
    before: 'Arrange berth or yard entry for the boat cleaner and check that the agreed deck or cabin areas are accessible during the visit.',
    after: 'Let the owner know when the clean is complete and agree a later upkeep date if the vessel needs regular attention.',
  },
  MP258: {
    before: 'Get a reliable roadside or storage-location contact and confirm where the recovery vehicle can safely reach the stranded vehicle.',
    after: 'Confirm the vehicle’s delivery and payment with the customer, while any transport or incident documentation stays in the operator’s own system.',
  },
  MP259: {
    before: 'Ask the caravan or motorhome owner which systems need attention and when the storage site or pitch can admit the technician.',
    after: 'Arrange any parts-fitting return with the owner and issue the service charge once the agreed work is complete.',
  },
  MP260: {
    before: 'Reserve the heavy vehicle’s repair slot with its fleet contact so the truck is off the road and accessible at the depot.',
    after: 'Coordinate the next maintenance date or parts visit with the operator, keeping technical service records outside ordinary business admin.',
  },
  MP261: {
    before: 'Check who can open the vessel and engine compartment for the marine engineer, with travel time to the berth included.',
    after: 'Give the boat owner a date for any further service visit and keep the technical work log in the engineer’s specialist files.',
  },
  MP262: {
    before: 'Confirm that the motor mechanic has a workable place to inspect the vehicle and that the owner can describe the issue on arrival.',
    after: 'Separate the diagnosis charge from a later repair quote, then agree a return appointment if parts are required.',
  },
  MP263: {
    before: 'Check that the motorcycle is accessible where it is stored and that the repair visit has enough time for diagnosis before fitting.',
    after: 'Tell the rider when the bike can be collected or a second visit booked, with any parts cost made clear in advance.',
  },
  MP264: {
    before: 'Arrange site entry and a safe shutdown window for the plant mechanic, allowing time to reach the machine within the worksite.',
    after: 'Agree a parts or test-run return with the site manager and keep detailed machinery records in the contractor’s own system.',
  },
  MP265: {
    before: 'Ask whether the trailer will be empty and parked on level ground for the technician to examine its coupling, lights or running gear.',
    after: 'Arrange a collection or further repair date if needed, and send the owner the agreed charge for the work completed.',
  },
  MP266: {
    before: 'Verify the replacement tyres are ready and that the vehicle can be parked somewhere the fitter can work safely at the appointed time.',
    after: 'Confirm the fitting charge and any further tyre appointment, with disposal and technical records handled in the fitter’s normal process.',
  },
  MP267: {
    before: 'Ask the vehicle owner to show the damaged panels and confirm whether the body repairer has a suitable sheltered working area.',
    after: 'Provide a date for finishing or collecting the vehicle if paint needs more time, then follow through on the agreed repair invoice.',
  },
  MP268: {
    before: 'Check the replacement glass has arrived and reserve a fitting slot where the vehicle can stay still and be reached on all sides.',
    after: 'Tell the driver when the vehicle can be used again according to the technician’s guidance and close out the payment.',
  },
  MP269: {
    before: 'Agree what the vehicle inspector has been asked to check and ensure the vehicle, keys and owner contact will be present.',
    after: 'Set a time to share the inspection outcome and keep the formal report in the provider’s own records, separate from booking notes.',
  },
  MP270: {
    before: 'Check whether the valet is booked for a quick interior clean or a full detail, and arrange access to the parked vehicle.',
    after: 'Confirm when the car is ready for handover and offer a separate future upkeep slot if the owner wants a regular clean.',
  },
  MP271: {
    before: 'Make sure the approved vehicle-wrap artwork and materials are ready before reserving a bay for the installation.',
    after: 'Arrange the customer’s collection time and issue the installation invoice, keeping design approvals in the wrapper’s project files.',
  },
  MP272: {
    before: 'Ask the customer to clear access around the faulty appliance and make a note of whether it is built into cabinetry.',
    after: 'If a replacement part is needed, agree a separate fitting date and tell the customer what the first visit will cost.',
  },
  MP273: {
    before: 'Set aside time for the person using the assistive device to be present, so the installer can check the position and explain operation.',
    after: 'Offer an agreed adjustment or handover visit if needed, keeping user assessments and care information in the appropriate specialist system.',
  },
  MP274: {
    before: 'Arrange a slot when the instrument can be removed from service for calibration and its operator can provide the right access.',
    after: 'Give the client a date for the calibration output or return visit, with certificates held in the technician’s professional records.',
  },
  MP275: {
    before: 'Book the coffee-machine engineer for a quieter trading period and check who can switch off the machine and open the service area.',
    after: 'Agree a parts-fitting visit if the machine cannot be repaired at once, then send the café the agreed service invoice.',
  },
  MP276: {
    before: 'Find a kitchen downtime window for the catering-equipment engineer and confirm which appliance can be taken out of use.',
    after: 'Tell the venue when further parts work can happen, keeping any technical service or safety documentation in its specialist process.',
  },
  MP277: {
    before: 'Coordinate with the laundry manager so the affected washer or dryer is empty and available when the engineer arrives.',
    after: 'Plan a return fitting visit around the next quiet period if parts are needed, and keep the repair charge clear for the operator.',
  },
  MP278: {
    before: 'Agree whether the computer technician will attend the office or collect the device, and who can authorise access when they arrive.',
    after: 'Confirm the handback or next repair slot and invoice the business, while any device data stays within the technician’s secure process.',
  },
  MP279: {
    before: 'Arrange a safe handover of the faulty electronic device and agree whether the first appointment is assessment or repair.',
    after: 'Give the owner a collection date once parts and repair time are known, with costs confirmed before additional work is booked.',
  },
  MP280: {
    before: 'Reserve a dental-equipment service window between patient appointments and make sure a clinic contact can identify the affected unit.',
    after: 'Coordinate any return service with the practice manager, keeping technical and clinical equipment records in the appropriate system.',
  },
  MP281: {
    before: 'Ask the gym to reserve the affected exercise machine during a quiet period and clear access around it for the technician.',
    after: 'Agree a repair or testing return date if needed, and send the operator an invoice for the completed service stage.',
  },
  MP282: {
    before: 'Set an IT support slot when the affected staff and devices are available, with a named person to approve any interruption.',
    after: 'Arrange a follow-up visit for unresolved equipment work and handle the service invoice, leaving credentials and incident detail in secure tools.',
  },
  MP283: {
    before: 'Book laboratory instrument work when the unit is free and an authorised lab contact can arrange entry to the equipment area.',
    after: 'Confirm a return date or report handover, keeping calibration, validation and technical results in the lab’s specialist records.',
  },
  MP284: {
    before: 'Coordinate a medical-device service slot with the department so the equipment is available without disrupting scheduled use.',
    after: 'Tell the site contact when any further engineer visit is planned, with device servicing evidence retained in the approved technical system.',
  },
  MP285: {
    before: 'Make sure the mobility device and its user are available together for fitting or adjustment at the agreed visit.',
    after: 'Book a separate check or repair return if needed, while user-specific assessment and safety information remains in suitable specialist records.',
  },
  MP286: {
    before: 'Agree a network maintenance window and arrange access to the cabinets and site contact before the engineer travels.',
    after: 'Schedule any remaining installation or fault work with the business and keep configuration and access credentials in secure IT systems.',
  },
  MP287: {
    before: 'Check the phone or tablet is ready for handover and agree whether the first slot covers diagnosis or a known screen replacement.',
    after: 'Tell the owner when to collect the repaired device and confirm any added parts cost before completing the job.',
  },
  MP288: {
    before: 'Arrange office access for the printer technician and ask for the machine’s location so the callout does not lose time finding it.',
    after: 'If a part or supply is outstanding, agree a return date and invoice the organisation for the service already provided.',
  },
  MP289: {
    before: 'Check whether the vending-machine booking is for replenishment or repair and arrange a visit that suits the site’s opening hours.',
    after: 'Set the next refill or repair visit with the site contact, keeping stock and machine telemetry in the operator’s own systems.',
  },
  MP290: {
    before: 'Reserve a time when the weighing equipment is not needed and the technician can reach it without interrupting the operation.',
    after: 'Confirm when the scale can return to use and arrange the next service date, with formal calibration evidence kept separately.',
  },
  MP291: {
    before: 'Coordinate the compressed-air service with production so the compressor can be isolated and the engineer can reach the plant room.',
    after: 'Agree a further repair window if the first visit finds parts are needed, and send the business the service-stage invoice.',
  },
  MP292: {
    before: 'Ask the site operator when the generator can be inspected and whether a temporary power plan affects the visit window.',
    after: 'Put any load test or parts return in the calendar, with engineering results and safety documents kept in specialist records.',
  },
  MP293: {
    before: 'Arrange a stopped-machine window for the hydraulic engineer and make sure the leak or failure area can be reached safely.',
    after: 'Confirm when replacement components and another visit are expected, keeping pressure-test evidence in the engineer’s own files.',
  },
  MP294: {
    before: 'Book industrial cleaning when the area can be released by the site and the crew has a suitable loading and access route.',
    after: 'Agree the next cleaning block or handover with the contract contact, keeping hazardous-work documentation in the proper specialist process.',
  },
  MP295: {
    before: 'Check that the machinery delivery, lifting team and installation space are aligned before committing an installer crew to site.',
    after: 'Arrange any commissioning or snagging return with the site manager and follow up on the agreed installation-stage invoice.',
  },
  MP296: {
    before: 'Reserve a maintenance slot when the industrial machine can stop and a site engineer can explain what has changed since the last service.',
    after: 'Book outstanding parts work against the same machine and keep detailed maintenance evidence in the operator’s technical system.',
  },
  MP297: {
    before: 'Arrange building access to the lift and machinery space, with a manager available to take the lift out of service if needed.',
    after: 'Agree any further engineer visit and inform the building contact of the timing, while compliance records stay in specialist systems.',
  },
  MP298: {
    before: 'Ask the facilities contact which mechanical plant can be inspected and when it can be taken offline without disrupting the building.',
    after: 'Schedule a separate repair or review slot after diagnosis, keeping plant drawings and commissioning records in project files.',
  },
  MP299: {
    before: 'Confirm the component is ready and accessible for non-destructive testing and agree who can provide the required site induction.',
    after: 'Set a date for the inspection report and any retest, with technical results managed in the testing provider’s own system.',
  },
  MP300: {
    before: 'Coordinate the racking crew’s arrival with stock movement and ensure the floor area and delivered bays are ready for fitting.',
    after: 'Agree a handover or snagging date with the warehouse manager, keeping load and installation documentation in specialist files.',
  },
  MP301: {
    before: 'Arrange the welding slot when the workpiece can be reached and the site has approved the required hot-work arrangements.',
    after: 'Confirm any follow-on fabrication visit and invoice the agreed stage, keeping weld inspection and safety evidence in your own records.',
  },
  MP302: {
    before: 'Check the awning dimensions and wall access before booking a fitting day, leaving room to move the visit if weather makes installation unsafe.',
    after: 'Agree a handover or adjustment date with the owner and send the installation invoice once the agreed fitting is complete.',
  },
  MP303: {
    before: 'Ask whether the clock repairer is collecting a small movement or attending a large clock in place, then arrange a careful handover.',
    after: 'Tell the owner when the clock can be returned or tested again, with workshop restoration detail kept outside booking notes.',
  },
  MP304: {
    before: 'Book the curtain or blind measure with access to every window, then reserve fitting time only after the products are ready.',
    after: 'Confirm the installation date and any final payment, leaving a separate appointment for adjustments if the client needs them.',
  },
  MP305: {
    before: 'Check whether the furniture can stay in a ventilated space while French polish dries, and plan the visit around other use of the room.',
    after: 'Arrange a later inspection or collection time if the finish needs curing, then close out the restoration invoice.',
  },
  MP306: {
    before: 'Ask the client to have all flat-pack boxes and fittings ready and clear enough floor space before the furniture assembler arrives.',
    after: 'Confirm whether any missing parts require another assembly slot and send the charge for items completed on the first visit.',
  },
  MP307: {
    before: 'Arrange a furniture-restoration assessment with the owner present, then decide separately when the piece can be collected.',
    after: 'Give the owner an agreed workshop update and return date, keeping material choices and conservation notes in the restorer’s files.',
  },
  MP308: {
    before: 'Have the leather item available for inspection in good light and confirm whether it can be taken away for repair.',
    after: 'Agree a return or collection appointment once colour and repair work are complete, and follow up on the approved cost.',
  },
  MP309: {
    before: 'Check whether the instrument repairer can work at the venue or needs the instrument transported to a workshop.',
    after: 'Let the musician know when testing or collection can happen, keeping specialist repair detail with the instrument’s work order.',
  },
  MP310: {
    before: 'Reserve a quiet, uninterrupted period for piano tuning and make sure the room and instrument will be accessible.',
    after: 'Confirm the tuning charge and agree when the next visit should be considered, without turning a future date into a guaranteed interval.',
  },
  MP311: {
    before: 'Arrange a sewing-machine service when the operator can stop using it and provide the accessories needed to reproduce the problem.',
    after: 'Confirm any parts-fitting return or workshop handback, and invoice the agreed diagnosis or repair stage.',
  },
  MP312: {
    before: 'Make sure the sign is approved and ready before arranging access equipment and a fitting slot at the premises.',
    after: 'Agree a handover time with the business and keep any structural or installation evidence in specialist project records.',
  },
  MP313: {
    before: 'Ask the customer to clear the damaged worktop, bath or other surface so the repairer can match the finish and protect nearby areas.',
    after: 'Tell the customer when the repaired surface can be used under the technician’s guidance, and settle the agreed fee.',
  },
  MP314: {
    before: 'Count the blades or tools for sharpening and agree a collection slot that does not leave a business without essential equipment.',
    after: 'Confirm the return handover and charge for the completed batch, with another booking for tools that were not ready.',
  },
  MP315: {
    before: 'Check the upholstery fabric choice and collection access with the owner before reserving workshop time for the furniture.',
    after: 'Arrange delivery or a fitting visit when the piece is ready, and follow up on the balance for the approved work.',
  },
  MP316: {
    before: 'Confirm the cabinets and cut-out positions are final before the worktop fitter’s measure, then schedule fitting after delivery.',
    after: 'Set a handover date and any snagging visit separately, keeping the material order and fitting invoice linked to the job.',
  },
  MP317: {
    before: 'Talk through boarding and assistance arrangements with the passenger or organiser, then allow enough time at both ends of the journey.',
    after: 'Confirm completion with the booking contact and arrange the next journey if requested, keeping care or mobility assessments in specialist systems.',
  },
  MP318: {
    before: 'Coordinate the boat transporter’s arrival with lifting crews at collection and delivery, allowing for access at both sites.',
    after: 'Confirm the vessel has been handed over and settle the transport invoice, while movement permits and technical documents stay in proper records.',
  },
  MP319: {
    before: 'Check that the caravan mover can enter both pitches and that someone will be available to release and receive the caravan.',
    after: 'Tell the owner when the move is complete and handle payment, booking any later repositioning as a separate journey.',
  },
  MP320: {
    before: 'Review the chauffeur itinerary with the booking contact, including each stop and the time passengers need to be ready.',
    after: 'Confirm any changed waiting time or return trip with the client before invoicing the agreed journey.',
  },
  MP321: {
    before: 'Check coach pickup points and the venue arrival deadline with the organiser, allowing realistic boarding time for the group.',
    after: 'Confirm the return departure arrangements and send the organiser the final trip invoice once the journey is complete.',
  },
  MP322: {
    before: 'Arrange the courier collection so the parcel and sender are ready, then check the receiver’s opening hours before dispatch.',
    after: 'Confirm the delivery handover and invoice the job, keeping proof-of-delivery data in the courier’s usual operational system.',
  },
  MP323: {
    before: 'Coordinate the machinery mover’s crew with lifting access and a cleared route at both premises before setting the move date.',
    after: 'Confirm handover at the destination and follow up on the agreed transport charge, with lifting plans held in specialist files.',
  },
  MP324: {
    before: 'Check the day’s meal-delivery list against kitchen dispatch and confirm any changed addresses before the driver leaves.',
    after: 'Tell the organiser about any missed handover and plan the next round, keeping dietary and welfare information in suitable systems.',
  },
  MP325: {
    before: 'Allow for boarding and the appointment arrival time when planning non-emergency patient transport, and verify the correct pickup contact.',
    after: 'Confirm the return collection if booked and handle the journey payment, leaving patient care details in approved transport or clinical records.',
  },
  MP326: {
    before: 'Check the pet’s carrier and the availability of people at collection and handover before fixing the transport route.',
    after: 'Confirm the animal reached the receiving contact and arrange any later trip separately, with welfare notes kept in the provider’s own process.',
  },
  MP327: {
    before: 'Check the private-hire pickup location against the actual entrance, and allow time for luggage or an airport collection.',
    after: 'Confirm any booked return leg and settle the fare, using the operator’s normal system for journey and licensing records.',
  },
  MP328: {
    before: 'Reserve a removals survey before moving day when the load or stairs are uncertain, and confirm parking at both addresses.',
    after: 'Agree the unloading handover and final charge with the customer, then book any extra collection as its own move.',
  },
  MP329: {
    before: 'Plan the art handler’s packing and lift access with both venues, ensuring the piece is ready for a careful collection.',
    after: 'Confirm the installation or delivery handover and invoice the transport, keeping condition reports and provenance in specialist records.',
  },
  MP330: {
    before: 'Ask the taxi passenger for an exact pickup point and check whether luggage or accessible boarding affects the vehicle and timing.',
    after: 'Agree any return pickup before leaving and settle the fare through the driver’s usual business process.',
  },
  MP331: {
    before: 'Check whether the vehicle transporter will collect a running car or one that needs special loading, with keys ready at both ends.',
    after: 'Confirm delivery to the receiving contact and send the transport invoice, keeping movement paperwork in the operator’s own files.',
  },
  MP332: {
    before: 'Coordinate the agricultural contractor’s field slot with the farm manager and leave room for weather to shift machinery work.',
    after: 'Confirm the next field or work stage and invoice the completed operation, with crop and machinery records kept in farm systems.',
  },
  MP333: {
    before: 'Arrange a field walkthrough for the drainage contractor before reserving excavation equipment and a weather-sensitive work period.',
    after: 'Agree the next installation or reinstatement date with the farmer and follow up on the quoted stage of work.',
  },
  MP334: {
    before: 'Choose an agronomy visit when the crop can be assessed and identify which fields the adviser should walk with the grower.',
    after: 'Set a date for the next field discussion, keeping agronomic advice and sample findings in the adviser’s specialist records.',
  },
  MP335: {
    before: 'Ask the arboricultural client which trees need advice and arrange access for the consultant to inspect them in person.',
    after: 'Book the report discussion or a follow-up site meeting, with tree condition evidence and formal recommendations kept in professional files.',
  },
  MP336: {
    before: 'Confirm which part of the land the contaminated-site team can enter and coordinate fieldwork with any ground disturbance planned.',
    after: 'Arrange the results meeting after laboratory work, while sampling evidence and remediation advice stay in specialist project records.',
  },
  MP337: {
    before: 'Check that an ecology survey falls in the right season for the question and that the surveyor can reach the habitat in scope.',
    after: 'Set a date to review the ecological findings and any further field visits, keeping species data in appropriate professional records.',
  },
  MP338: {
    before: 'Agree whether the environmental consultant’s visit is for site observation, stakeholder discussion or sampling coordination.',
    after: 'Confirm the next report or decision meeting with the client, keeping environmental evidence in the project’s specialist files.',
  },
  MP339: {
    before: 'Arrange safe access for the geotechnical team and confirm that proposed borehole positions can be reached by their equipment.',
    after: 'Book the ground-results discussion or further investigation date, with logs and engineering interpretations held in project records.',
  },
  MP340: {
    before: 'Arrange a viewing of the antique with the owner before allocating restoration time or deciding whether it can travel to a workshop.',
    after: 'Give the owner a date for the next progress conversation and return, keeping condition details in the restorer’s own records.',
  },
  MP341: {
    before: 'Ensure the art conservator can examine the object in suitable light and discuss safe handling with its custodian.',
    after: 'Agree a treatment-review or handback date, with conservation decisions and imagery retained in specialist files.',
  },
  MP342: {
    before: 'Check that the artwork, fixing points and display wall will all be ready when the art-installation crew reaches the venue.',
    after: 'Confirm the finished position with the curator and arrange any later move or removal as a new appointment.',
  },
  MP343: {
    before: 'Agree the drone operator’s filming brief and site contact, then reserve a weather-flexible flight window after permissions are checked.',
    after: 'Tell the client when the selected footage will be delivered, with flight logs and permissions kept in the operator’s own system.',
  },
  MP344: {
    before: 'Check whether the fashion stylist is attending a fitting, shoot or wardrobe session so the people and garments are ready together.',
    after: 'Arrange garment returns or the next fitting with the client and invoice the completed styling stage separately.',
  },
  MP345: {
    before: 'Book livestream setup well ahead of the broadcast and confirm that the producer can test the venue connection and speakers.',
    after: 'Agree when the recording or agreed clips will be handed over, then close out any remaining production fee.',
  },
  MP346: {
    before: 'Check the mural artist has final approval for the design and access to the prepared wall before reserving painting days.',
    after: 'Confirm a completion or touch-up visit with the client, allowing any outdoor work to move if the weather changes.',
  },
  MP347: {
    before: 'Schedule photo-booth load-in before guests arrive and verify the operator can reach the allocated space and power supply.',
    after: 'Confirm collection after the event and tell the organiser when any agreed photographs will be available.',
  },
  MP348: {
    before: 'Run through the photographer’s shot priorities and venue arrival time, leaving enough space for travel and changing light.',
    after: 'Give the client the agreed image-selection or delivery date and handle any balance once the shoot is complete.',
  },
  MP349: {
    before: 'Arrange a portrait sitting when the sitter has enough uninterrupted time and the artist has the agreed reference material.',
    after: 'Book a review of the portrait before final handover, with any framing or delivery charge confirmed separately.',
  },
  MP350: {
    before: 'Match the set dresser’s arrival to prop delivery and the production call sheet, allowing time to dress the space before filming.',
    after: 'Agree when the set needs resetting or clearing and invoice the agreed production days after the handover.',
  },
  MP351: {
    before: 'Check the signwriter has approved wording and access to the surface for preparation before any on-site painting is booked.',
    after: 'Arrange a completion check after the lettering has dried and settle the agreed fee with the business.',
  },
  MP352: {
    before: 'Arrange a quiet recording window and make sure contributors know when they need to be present for the sound recordist.',
    after: 'Confirm the audio handoff date and any pickup session separately, keeping source recordings in the production files.',
  },
  MP353: {
    before: 'Check that the videographer’s contributors, locations and permissions line up with the shoot schedule before reserving travel time.',
    after: 'Tell the client when the first edit will be ready and book any additional filming separately from revision discussions.',
  },
  MP354: {
    before: 'Confirm the actor’s rehearsal and performance call times with the production contact, including where costume changes will happen.',
    after: 'Arrange any recall or extra performance day as a new booking and invoice the agreed work block.',
  },
  MP355: {
    before: 'Check that audiovisual kit can be loaded into the venue before rehearsal and that the technician has a contact for the house system.',
    after: 'Agree equipment strike and collection times with the organiser, then reconcile the hire or technical-service charge.',
  },
  MP356: {
    before: 'Reserve a balloon-decorating installation window before the venue opens and confirm where the display can be assembled.',
    after: 'Book removal after the event if it forms part of the agreement, and follow up on the decoration balance.',
  },
  MP357: {
    before: 'Ask the organiser where the balloon modeller can stand and how long they want guest interactions to run during the party.',
    after: 'Confirm any extension to the performance time with the organiser before adding it to the final invoice.',
  },
  MP358: {
    before: 'Arrange a well-lit chair and drawing space for the caricaturist, with a start time that fits the guest arrival pattern.',
    after: 'Settle the booked drawing period with the host and make a fresh booking if finished portraits need a separate handover.',
  },
  MP359: {
    before: 'Book the celebrant’s planning conversation before the ceremony date and confirm who will approve the final wording.',
    after: 'Arrange the script review or rehearsal at a separate time, keeping sensitive family details in the celebrant’s own records.',
  },
  MP360: {
    before: 'Check the children’s entertainer has the agreed age group, party start time and a clear setup space before arriving.',
    after: 'Confirm any extra performance time with the parent or organiser and send the agreed event invoice.',
  },
  MP361: {
    before: 'Reserve the circus performer’s rehearsal or equipment-check slot before guests enter, with the venue manager available to discuss space.',
    after: 'Coordinate strike time after the act and handle the performance balance, keeping safety documentation in the performer’s own process.',
  },
  MP362: {
    before: 'Confirm the comedian’s set length and exact cue in the running order, then check who will provide the microphone.',
    after: 'Settle the performance fee with the organiser and book any additional set as a separate event commitment.',
  },
  MP363: {
    before: 'Give dancers enough time to test the floor and run music cues before the audience arrives, including space for costume changes.',
    after: 'Confirm the final set count and fee with the organiser, keeping rehearsal or future bookings distinct from the event itself.',
  },
  MP364: {
    before: 'Plan the DJ’s load-in and sound check around venue access, then confirm the timings of any important music moments.',
    after: 'Agree the final finish time before adding overtime to the invoice and arrange equipment collection after guests leave.',
  },
  MP365: {
    before: 'Use the event-planning call to settle which supplier decisions are due next, not just the date of the event itself.',
    after: 'Book the next client review against the upcoming venue or supplier deadline, with contracts managed in the planner’s own files.',
  },
  MP366: {
    before: 'Coordinate the rigger’s access with the venue and other crews so overhead work is finished before later setup begins.',
    after: 'Agree the de-rig slot and invoice the installation stage, keeping rigging plans and inspection evidence in specialist records.',
  },
  MP367: {
    before: 'Check the steward’s reporting point and briefing time before the shift, including who will cover their assigned area.',
    after: 'Confirm the shift handover with the organiser and submit the agreed hours, while incident details stay in the event’s proper reporting system.',
  },
  MP368: {
    before: 'Check the event stylist has an approved look and a floor-plan position for each piece before venue setup begins.',
    after: 'Book the clearance window with the venue and reconcile any extra hired items against the styling invoice.',
  },
  MP369: {
    before: 'Set a face-painting start time after the artist has had access to a clean, well-lit station and the organiser knows the queue plan.',
    after: 'Confirm the booked finish and any extension with the host, then settle the agreed event charge.',
  },
  MP370: {
    before: 'Check the florist’s ceremony and reception deliveries separately, with each arrangement assigned to the right venue contact.',
    after: 'Arrange collection of any hired vases or structures and send the final invoice after the event installation.',
  },
  MP371: {
    before: 'Reserve the lighting technician’s rig and cue-check time before rehearsal, with access to the venue’s power and control positions.',
    after: 'Coordinate the strike schedule and any return for adjustments, keeping technical plots in the show’s production files.',
  },
  MP372: {
    before: 'Find out when the magician should perform among guests or on stage, and arrange a private place to prepare props.',
    after: 'Confirm any extra performance period with the organiser and invoice only the agreed time or event package.',
  },
  MP373: {
    before: 'Check that the marquee site is clear for delivery and that installation can finish before other event suppliers arrive.',
    after: 'Agree a separate dismantling slot with the venue contact and follow up on the hire balance after collection.',
  },
  MP374: {
    before: 'Give the master of ceremonies the final speaker list and introductions before guests arrive, then agree who will relay timing changes.',
    after: 'Confirm any extended hosting time with the organiser and close out the agreed event fee after the programme finishes.',
  },
  MP375: {
    before: 'Check the musician’s set and break times against the programme, and reserve a separate sound-check window.',
    after: 'Arrange instrument load-out after the performance and settle any additional set time agreed with the booker.',
  },
  MP376: {
    before: 'Agree with the party host who leads each game or announcement and when food and entertainment interrupt the activities.',
    after: 'Confirm the final hosting hours with the client and handle the fee, keeping any later party date as a separate booking.',
  },
  MP377: {
    before: 'Check the security officer’s post, shift handover and site contact before coverage starts, including where the briefing happens.',
    after: 'Submit the hours to the client and book the next shift, keeping any incident reports in the employer’s approved security system.',
  },
  MP378: {
    before: 'Confirm the singer’s accompaniment and cue for each song before sound check, especially if the performance spans a ceremony.',
    after: 'Settle the performance fee and agree a separate date if the client requests another set or rehearsal.',
  },
  MP379: {
    before: 'Arrange for the sound engineer to test inputs with performers before doors open, allowing time for problems in the venue system.',
    after: 'Coordinate pack-down after the show and invoice the agreed technical shift, with production settings kept in show files.',
  },
  MP380: {
    before: 'Match the stage technician’s call time to the first set change or load-in task, with cues confirmed by stage management.',
    after: 'Confirm the last strike or handover duty before closing the shift and sending the agreed invoice.',
  },
  MP381: {
    before: 'Choose a meeting point the tour group can recognise and check the route timing before confirming the guide’s start slot.',
    after: 'Tell the organiser when the tour has finished and settle the group charge, with any additional route booked separately.',
  },
  MP382: {
    before: 'Confirm final catering numbers and meal service times with the host before committing kitchen preparation and delivery slots.',
    after: 'Arrange equipment collection and final payment after the event, keeping dietary and food-safety records in the caterer’s own process.',
  },
  MP383: {
    before: 'Agree when event waiters should report for the venue briefing and leave time for table setup before guests are seated.',
    after: 'Confirm the service hours and clearing handover with the caterer, then submit the agreed shift charge.',
  },
  MP384: {
    before: 'Check the food truck’s pitch and arrival gate, allowing enough time to set up before the event’s serving window.',
    after: 'Confirm when the vehicle can leave and reconcile the event fee, keeping food-safety and trading records in the operator’s own system.',
  },
  MP385: {
    before: 'Arrange the mobile barista’s setup before the first break and check that the water and power arrangements are ready.',
    after: 'Confirm the final drinks-service period with the organiser and follow up on the agreed charge or any booked return day.',
  },
  MP386: {
    before: 'Check what bar stock the mobile bartender is providing and when the venue will allow glassware and station setup.',
    after: 'Agree the close-down and stock handover with the host, then settle the agreed service fee in the usual business process.',
  },
  MP387: {
    before: 'Discuss the private-chef menu and kitchen access in advance, then reserve enough time for shopping, cooking and clearing.',
    after: 'Confirm the finished service with the host and invoice the agreed meal, keeping dietary information in a suitable food-service process.',
  },
  MP388: {
    before: 'Align the sommelier’s tasting or service slot with the menu and ensure the wines can be prepared before guests sit down.',
    after: 'Send the host the agreed tasting follow-up or invoice, keeping any stock orders in the sommelier’s own records.',
  },
  MP389: {
    before: 'Agree which accounting period the meeting covers and ask the client to have their usual financial records available through a secure channel.',
    after: 'Set the next review or filing conversation in the diary, while accounts and confidential figures remain in professional software.',
  },
  MP390: {
    before: 'Confirm the auctioneer’s inspection and sale times with the venue, leaving enough time to review the final lot order.',
    after: 'Arrange the seller’s settlement conversation after the sale and keep bidding and ownership records in the auction system.',
  },
  MP391: {
    before: 'Book a bookkeeping session for a defined transaction period and arrange secure access to the source paperwork beforehand.',
    after: 'Confirm which missing documents the client still needs to provide and schedule the next accounts update, without placing financial detail in the diary.',
  },
  MP392: {
    before: 'Ask the business consultant which decision the workshop should help make and ensure the relevant managers can attend.',
    after: 'Agree the next decision meeting and invoice the completed session, with sensitive strategy material kept in the client’s project files.',
  },
  MP393: {
    before: 'Coordinate interviews with the teams responsible for the data flows under review, using secure channels for any documents.',
    after: 'Book the findings discussion and keep audit evidence or personal-data details in the consultant’s approved specialist system.',
  },
  MP394: {
    before: 'Make sure the estate agent can enter the property and knows whether the slot is for valuation, photographs or a buyer viewing.',
    after: 'Confirm the next viewing or seller call and manage offers and property documents through the agency’s usual system.',
  },
  MP395: {
    before: 'Check the field interviewer’s assignment area and interview window, with the survey briefing completed before travel.',
    after: 'Confirm the shift handover and book any further fieldwork, keeping responses and participant information in the research system.',
  },
  MP396: {
    before: 'Set a private financial-advice meeting and invite the client to share relevant papers only through the adviser’s secure process.',
    after: 'Arrange the next suitability or review meeting, with recommendations and confidential financial information kept in regulated systems.',
  },
  MP397: {
    before: 'Book the food-safety consultant when the kitchen is working normally, with a manager available to explain the service flow.',
    after: 'Set a date to discuss findings or revisit the premises, while inspection detail and compliance records stay in specialist files.',
  },
  MP398: {
    before: 'Arrange the health-and-safety walkthrough while the relevant activity is happening and confirm a supervisor can explain the site.',
    after: 'Book a separate review of recommendations, keeping risk assessments and incident material in appropriate professional records.',
  },
  MP399: {
    before: 'Choose a private room for the HR consultation and confirm who is attending without putting an employee’s issue in general booking notes.',
    after: 'Agree the next management discussion and follow up on fees, keeping personnel documents in secure HR systems.',
  },
  MP400: {
    before: 'Schedule the insurance-broker review ahead of renewal and ask the client to provide policy papers through a secure channel.',
    after: 'Agree when the client will consider cover options and book the next call, with policy and risk detail held in brokerage systems.',
  },
  MP401: {
    before: 'Arrange access to the damaged property for the loss adjuster and check that the insurer’s site contact can attend.',
    after: 'Set a date for the next claim discussion, keeping photographs, evidence and settlement work in the insurer’s specialist system.',
  },
  MP402: {
    before: 'Book the loss assessor’s visit when the policyholder can show the damage and provide supporting material securely.',
    after: 'Arrange a follow-up on the claim submission, with inventories and financial evidence managed outside the general appointment diary.',
  },
  MP403: {
    before: 'Confirm the interpreting language pair and meeting format, then allow the interpreter enough time to join before participants begin.',
    after: 'Agree any further interpreting session and submit the service fee, keeping the conversation content out of booking notes.',
  },
  MP404: {
    before: 'Find a neutral mediation setting and agree a session length with all parties, including time for separate breaks if needed.',
    after: 'Offer a next meeting date through the proper contact route, while confidential discussion and agreements stay in the mediator’s own system.',
  },
  MP405: {
    before: 'Arrange a private mortgage-advice conversation at the right stage of the purchase and use a secure route for financial papers.',
    after: 'Book the next application or review call and keep affordability figures and lender documents within the adviser’s regulated process.',
  },
  MP406: {
    before: 'Check the notarial appointment has the right signatories present and that the notary has reviewed document requirements in advance.',
    after: 'Confirm collection or onward delivery of the notarised papers, with identity checks and registers retained in the notary’s own system.',
  },
  MP407: {
    before: 'Agree the investigator’s authorised fieldwork period and a discreet meeting point, leaving case information in secure specialist records.',
    after: 'Schedule a confidential findings discussion with the client, keeping evidence and subject details out of ordinary booking notes.',
  },
  MP408: {
    before: 'Check that the process server has the correct papers and service address, then plan a visit window under the proper legal process.',
    after: 'Confirm the next attempt or client update without putting recipient details or proof of service in general business notes.',
  },
  MP409: {
    before: 'Clarify which vacancy the recruitment meeting covers and arrange interview slots that work for the hiring manager.',
    after: 'Book the next candidate or client conversation, with CVs, feedback and personal data kept in the recruitment system.',
  },
  MP410: {
    before: 'Set a private solicitor-client meeting and use the firm’s secure channel for matter documents rather than general appointment notes.',
    after: 'Confirm the next consultation or deadline discussion, keeping privileged advice and case material in the legal practice system.',
  },
  MP411: {
    before: 'Book the tax-advice meeting far enough ahead of the relevant deadline and arrange secure delivery of the client’s records.',
    after: 'Agree the next filing or review call, with tax calculations and supporting documents kept in the adviser’s professional software.',
  },
  MP412: {
    before: 'Confirm the valuer can inspect the asset and that the instructing party has stated the purpose of the valuation.',
    after: 'Arrange a date to deliver or discuss the valuation, with evidence and the formal report held in the valuer’s own system.',
  },
  MP413: {
    before: 'Choose a private will-writing appointment where the client can speak for themselves, with sensitive papers shared through a secure route.',
    after: 'Book the draft-review meeting separately and keep the client’s wishes and signed documents in the will writer’s specialist files.',
  },
  MP414: {
    before: 'Check the errand runner has a clear shopping or collection list and knows who can authorise spending before they set off.',
    after: 'Confirm the goods and receipts were handed over, then settle the agreed fee or book the next regular errand.',
  },
  MP415: {
    before: 'Arrange a quiet funeral-planning meeting with the person coordinating arrangements and check which venue dates are already fixed.',
    after: 'Confirm the next contact or supplier conversation, keeping personal arrangements and statutory paperwork in the funeral director’s own system.',
  },
  MP416: {
    before: 'Check the home stager can enter each room and that hired furniture can be delivered before the property photography date.',
    after: 'Agree the removal or refresh appointment with the seller and follow up on the staging hire balance.',
  },
  MP417: {
    before: 'Meet the house sitter for a key handover and walkthrough of the home’s routine before the owner leaves.',
    after: 'Confirm the departure and key return with the owner, keeping access codes and household information in a secure process.',
  },
  MP418: {
    before: 'Agree which life-admin tasks are in scope and what the client has authorised before booking time for calls or paperwork.',
    after: 'Arrange the next task session and give a concise progress update, with personal documents managed through the client’s chosen secure system.',
  },
  MP419: {
    before: 'Check which diary, travel or correspondence tasks the personal assistant should cover and who can approve decisions.',
    after: 'Confirm completed actions and the next work block, keeping private messages and account access out of general booking notes.',
  },
  MP420: {
    before: 'Book the organising session when the person deciding what to keep can be present and the chosen room is available.',
    after: 'Agree a separate donation, removal or next-room visit if needed, then invoice the completed organising time.',
  },
  MP421: {
    before: 'Map the relocation client’s immediate decisions before the move and book meetings with the relevant local contacts.',
    after: 'Set the next settling-in appointment and keep housing or school documents in the client’s appropriate project files.',
  },
  MP422: {
    before: 'Arrange the senior move meeting around the older person’s pace and ensure their chosen contact can join decisions.',
    after: 'Book packing and new-home handover as distinct steps, keeping personal circumstances and care information in suitable private records.',
  },
  MP423: {
    before: 'Check the archive conservator has a clean workspace and authorised access to the material before setting an examination time.',
    after: 'Arrange the next treatment or handback discussion, with object condition and conservation evidence kept in specialist files.',
  },
  MP424: {
    before: 'Agree which collection and storage areas the inventory specialist can access, and arrange an authorised custodian on site.',
    after: 'Set a date to reconcile any missing or moved objects, keeping detailed identifiers in the collection’s own management system.',
  },
  MP425: {
    before: 'Check the scanning team has a secure handover point and time to prepare the documents before digitisation begins.',
    after: 'Confirm file delivery and return of originals with the client, keeping document contents and custody logs in approved secure systems.',
  },
  MP426: {
    before: 'Arrange authorised site or device access for the forensic investigator and keep the assignment brief in a secure case system.',
    after: 'Book a confidential findings meeting, while evidence, chain of custody and sensitive case details remain in specialist records.',
  },
  MP427: {
    before: 'Confirm the marine surveyor can access the vessel at its berth and reserve a separate lift-out slot if the scope requires it.',
    after: 'Agree when the client will discuss the survey report, keeping technical findings and vessel evidence in professional files.',
  },
  MP428: {
    before: 'Schedule the occupational hygienist while the work activity can be observed and arrange safe access to the relevant areas.',
    after: 'Set a time to explain sampling results, with exposure data and formal workplace assessments kept in specialist systems.',
  },
  MP429: {
    before: 'Arrange point-of-sale repair during a quieter trading period and confirm which till or terminal the technician can take offline.',
    after: 'Book any further parts visit and invoice the service, keeping payment credentials and transaction data out of the general diary.',
  },
});
